/**
 * BatchDialogs Component
 *
 * Renders the batch operation dialogs:
 * - Generate dialog
 * - Download dialog
 * - Email dialog
 *
 * Follows Single Responsibility Principle - only handles dialog UI
 */

import { Download, RefreshCw, Send, X, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { BatchDialogType, DownloadProgress } from "../store/types";

interface Template {
  id: number;
  name: string;
}

interface SelectedReportsData {
  withReport: number[];
  withoutReport: number[];
}

interface BatchDialogsProps {
  /** Which dialog is currently open */
  openDialog: BatchDialogType;
  /** Close the current dialog */
  onClose: () => void;
  /** Number of selected submissions */
  selectedCount: number;
  /** Whether any selected has a report */
  hasAnyReport: boolean;
  /** Available templates */
  templates: Template[] | undefined;
  /** Currently selected template ID */
  templateId: string;
  /** Callback when template changes */
  onTemplateChange: (id: string) => void;
  /** Notify option for generate dialog */
  notify: boolean;
  /** Callback when notify changes */
  onNotifyChange: (value: boolean) => void;
  /** Selected reports data for download/email */
  selectedReportsData: SelectedReportsData | undefined;
  /** Whether generating is in progress */
  isGenerating: boolean;
  /** Whether downloading is in progress */
  isDownloading: boolean;
  /** Whether sending emails is in progress */
  isSendingEmails: boolean;
  /** Download progress */
  downloadProgress: DownloadProgress;
  /** Callback to generate reports */
  onGenerate: () => void;
  /** Callback to download reports */
  onDownload: (onlyExisting: boolean) => void;
  /** Callback to send emails */
  onSendEmails: () => void;
}

/**
 * Batch dialogs component
 *
 * @example
 * ```tsx
 * <BatchDialogs
 *   openDialog={batchDialog.open}
 *   onClose={closeDialog}
 *   selectedCount={selectedCount}
 *   hasAnyReport={selectedHaveAnyReport}
 *   templates={templates}
 *   templateId={batchDialog.templateId}
 *   onTemplateChange={setBatchTemplate}
 *   notify={batchDialog.notify}
 *   onNotifyChange={setBatchNotify}
 *   selectedReportsData={selectedReportsData}
 *   isGenerating={isGenerating}
 *   isDownloading={isDownloading}
 *   isSendingEmails={isSendingEmails}
 *   downloadProgress={downloadProgress}
 *   onGenerate={handleBatchGenerate}
 *   onDownload={handleBulkDownload}
 *   onSendEmails={handleBulkEmail}
 * />
 * ```
 */
export function BatchDialogs({
  openDialog,
  onClose,
  selectedCount,
  hasAnyReport,
  templates,
  templateId,
  onTemplateChange,
  notify,
  onNotifyChange,
  selectedReportsData,
  isGenerating,
  isDownloading,
  isSendingEmails,
  downloadProgress,
  onGenerate,
  onDownload,
  onSendEmails,
}: BatchDialogsProps) {
  return (
    <>
      {/* Generate Dialog */}
      <Dialog open={openDialog === "generate"} onOpenChange={() => onClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {hasAnyReport ? "Regenerar" : "Generar"} Reportes
            </DialogTitle>
            <DialogDescription>
              Se {hasAnyReport ? "regenerarán" : "generarán"} reportes para{" "}
              {selectedCount} envío{selectedCount !== 1 ? "s" : ""}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Plantilla de reporte</label>
              <Select value={templateId} onValueChange={onTemplateChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Seleccionar plantilla" />
                </SelectTrigger>
                <SelectContent>
                  {templates?.map((template) => (
                    <SelectItem key={template.id} value={String(template.id)}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="batch-notify"
                checked={notify}
                onCheckedChange={(checked) => onNotifyChange(checked === true)}
              />
              <label htmlFor="batch-notify" className="text-sm">
                Enviar email al completar
              </label>
            </div>
          </div>

          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              onClick={onGenerate}
              disabled={!templateId || isGenerating}
              className="w-full sm:w-auto"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Encolando...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {hasAnyReport ? "Regenerar" : "Generar"} ({selectedCount})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Download Dialog */}
      <Dialog open={openDialog === "download"} onOpenChange={() => onClose()}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Descargar Reportes</DialogTitle>
            <DialogDescription>
              Se descargarán los reportes de {selectedCount} envío
              {selectedCount !== 1 ? "s" : ""} en un archivo ZIP.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Tipo de reporte</label>
              <Select value={templateId} onValueChange={onTemplateChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  {templates?.map((template) => (
                    <SelectItem key={template.id} value={String(template.id)}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Report availability summary */}
            {templateId && selectedReportsData && (
              <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Disponibles:</span>
                  <span className="font-medium text-green-600">
                    {selectedReportsData.withReport.length}
                  </span>
                </div>
                {selectedReportsData.withoutReport.length > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Sin reporte:</span>
                    <span className="font-medium text-orange-500">
                      {selectedReportsData.withoutReport.length}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Progress indicator */}
            {isDownloading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Descargando...</span>
                  <span>
                    {downloadProgress.current} / {downloadProgress.total}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{
                      width: `${(downloadProgress.current / downloadProgress.total) * 100}%`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-row justify-end gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={onClose}
                    disabled={isDownloading}
                    className="text-red-500 border-red-200 hover:bg-red-500 hover:text-white hover:border-red-500"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="bg-red-500 text-white border-red-500">
                  Cancelar
                </TooltipContent>
              </Tooltip>

              {selectedReportsData?.withoutReport.length ? (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="secondary"
                        size="icon"
                        onClick={() => onDownload(true)}
                        disabled={
                          !templateId ||
                          isDownloading ||
                          selectedReportsData.withReport.length === 0
                        }
                      >
                        {isDownloading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="font-medium">
                        Solo disponibles ({selectedReportsData.withReport.length}
                        )
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Descargar reportes ya generados
                      </p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        onClick={() => onDownload(false)}
                        disabled={!templateId || isDownloading}
                      >
                        {isDownloading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="font-medium">Generar y descargar</p>
                      <p className="text-xs text-muted-foreground">
                        Generar faltantes (
                        {selectedReportsData.withoutReport.length}) y descargar
                        todos
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon"
                      onClick={() => onDownload(true)}
                      disabled={
                        !templateId ||
                        isDownloading ||
                        (selectedReportsData?.withReport.length || 0) === 0
                      }
                    >
                      {isDownloading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-medium">Descargar ZIP</p>
                    <p className="text-xs text-muted-foreground">
                      Descargar todos los reportes
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={openDialog === "email"} onOpenChange={() => onClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enviar Reportes por Email</DialogTitle>
            <DialogDescription>
              Se enviará el reporte a {selectedCount} participante
              {selectedCount !== 1 ? "s" : ""}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 overflow-hidden">
            <div className="space-y-2">
              <label className="text-sm font-medium">Tipo de reporte</label>
              <Select value={templateId} onValueChange={onTemplateChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  {templates?.map((template) => (
                    <SelectItem key={template.id} value={String(template.id)}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Report availability summary */}
            {templateId && selectedReportsData && (
              <div className="p-3 bg-muted/50 rounded-lg space-y-2 overflow-hidden">
                <div className="flex items-center justify-between text-sm gap-2">
                  <span className="text-muted-foreground truncate">
                    Con reporte:
                  </span>
                  <span className="font-medium text-green-600 flex-shrink-0">
                    {selectedReportsData.withReport.length}
                  </span>
                </div>
                {selectedReportsData.withoutReport.length > 0 && (
                  <>
                    <div className="flex items-center justify-between text-sm gap-2">
                      <span className="text-muted-foreground truncate">
                        Sin reporte:
                      </span>
                      <span className="font-medium text-orange-500 flex-shrink-0">
                        {selectedReportsData.withoutReport.length}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Sin reporte recibirán email al generarse.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSendingEmails}
              className="w-full sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              onClick={onSendEmails}
              disabled={!templateId || isSendingEmails}
              className="w-full sm:w-auto"
            >
              {isSendingEmails ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {isSendingEmails ? "Enviando..." : "Enviar Emails"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
